import { useRef } from 'react';
import { compareDocumentPosition } from '../utils/dom.js';
import { getFirstTextInput } from '../utils/input.js';
import { moveSelectionToStart, moveSelectionToEnd } from '../utils/selection.js';

// This is based on anecdotal evidence from checking how long it takes
// for the detail property of a click event to reset.
// https://codepen.io/bschlenk/pen/jOQpbNr?editors=1010
const DOUBLE_CLICK_TIMEOUT = 500;
/**
 * Forwards pointerdown events to the input element within. Emulates the behavior such that
 * clicking on the element focus the input and moves the cursor to the end, and double-clicking
 * selects the input's contents.
 *
 * @returns A onPointerDown event handler to be passed to the root element.
 */
function useForwardFocusToInput(onPointerDownIn) {
    const lastPointerDownTime = useRef(0);
    function onPointerDown(e) {
        onPointerDownIn?.(e);
        // TODO: alternative for pen and touch
        // Touch and pen trigger focus on click, not pointerdown, so only handle this for mouse input.
        if (e.pointerType !== 'mouse')
            return;
        const target = e.target;
        if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement)
            return;
        const input = getFirstTextInput(e.currentTarget);
        if (!input)
            return;
        // Forward clicks to the input
        input.focus();
        // Allow buttons to customize this behavior further. We've already focused the input, but
        // the button will wind up receiving focus if it doesn't call preventDefault.
        if (target instanceof HTMLButtonElement)
            return;
        // pointerCapture to the input so that the remaining events in the chain act as if they were
        // always sent to the input.
        input.setPointerCapture(e.pointerId);
        // Really hacky but this lets us avoid a single frame of the focus ring disappearing
        // between the blur and the `setTimeout` running.
        input.setAttribute('data-faux-focus', '');
        // The pointerdown was on a different element, and pointerdown triggers focus. We could also
        // call `e.preventDefault()` to stop focus from changing, but that would also prevent dragging
        // to select text. So instead we focus _again_ in a timeout, so it happens just after the
        // browser natively focuses the element that was just clicked.
        setTimeout(() => {
            input.focus();
            input.removeAttribute('data-faux-focus');
        });
        // Tiny attention to detail, but when clicking on an input, clicking the left side moves the
        // cursor to the beginning, and clicking the right side moves the cursor to the end. We want
        // to emulate this behavior when forwarding focus.
        const pos = compareDocumentPosition(target, input);
        if (pos < 0) {
            moveSelectionToStart(input);
        }
        else {
            moveSelectionToEnd(input);
        }
        // Double click selects all text, so we implement that as well.
        const now = Date.now();
        if (now - lastPointerDownTime.current < DOUBLE_CLICK_TIMEOUT) {
            input.select();
        }
        lastPointerDownTime.current = now;
    }
    return onPointerDown;
}

export { useForwardFocusToInput };
//# sourceMappingURL=use-forward-focus-to-input.js.map
